import React, { useState } from "react";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import classnames from "classnames";

import IconButton from "@/shared/components/IconButton/IconButton";
import EgwWebFont from "@/shared/assets/EgwWebFont";
import { getMonthDetails } from "@/shared/components/DatePicker/DatePicker.utils";
import { formatToServerDate, isSameDateMonthYear } from "@/shared/utils/date";
import Typography from "@/shared/components/Typography/Typography";

import styles from "./DatePicker.module.scss";

const DAYS_IN_WEEK = 7;
const weekNames = new Array(DAYS_IN_WEEK).fill("");

const DatePicker = ({
  calendar,
  onFetchMonthData,
  date,
  minDate,
  maxDate,
  onDateChange,
}) => {
  const [monthDate, setMonthDate] = useState(date);

  const monthDateInstance = dayjs(monthDate);
  const currentDateInstance = dayjs(date);

  const monthDetails = getMonthDetails(monthDateInstance.year(), monthDateInstance.month());

  /**
   * @type {DayDetails[][]}
   */
  const weeks = [];

  for (let i = 0; i < monthDetails.length; i += DAYS_IN_WEEK) {
    const chunk = monthDetails.slice(i, i + DAYS_IN_WEEK);
    weeks.push(chunk);
  }

  const handleMonthChange = (newDate) => {
    setMonthDate(newDate);

    if (onFetchMonthData) {
      onFetchMonthData(newDate);
    }
  };

  const renderDay = ({
    timestamp,
    date,
    valid
  }, index) => {
    const dateInstance = dayjs(timestamp);
    const monthFinal = dateInstance.month() + 1;
    const dateInServerFormat = formatToServerDate(dateInstance);
    const validFinal = valid && (!maxDate || isSameDateMonthYear(dateInstance, maxDate) || dateInstance.isBefore(maxDate))
      && (!minDate || isSameDateMonthYear(dateInstance, minDate) || dayjs(dateInstance).isAfter(minDate));

    const year = dateInstance.year();

    const selected = isSameDateMonthYear(currentDateInstance, dateInstance);

    let read, unread, calendarSubsCount;

    const calendarMonthSubscriptionInfo = calendar?.[year]?.[monthFinal];
    if (calendarMonthSubscriptionInfo) {
      const dateInfo = calendarMonthSubscriptionInfo.find((dateItem) => dateItem.date === dateInServerFormat);

      if (dateInfo && !dateInstance.isAfter(new Date())) {
        const {
          unread: unreadCount,
          total
        } = dateInfo;

        calendarSubsCount = total;
        unread = calendarSubsCount && unreadCount !== 0;
        read = calendarSubsCount && unreadCount === 0;
      }
    }

    return (
      <button
        disabled={!validFinal || selected}
        key={index}
        className={classnames(styles["day"], {
          [styles["selected"]]: validFinal && selected,
          [styles["read"]]: read,
          [styles["unread"]]: unread,
          [styles["disabled"]]: !validFinal,
        })}
        onClick={() => {
          if (onDateChange) {
            const date = new Date(timestamp);
            date.setHours(12);
            onDateChange(date);
          }
        }}
      >
        {date}
        {!!calendarSubsCount && <div className={styles["indicator"]}/>}
      </button>
    );
  };

  const renderWeekName = (_, dayIndex) => {
    return (
      <Typography
        key={dayIndex}
        className={styles["week-name"]}
        variant={Typography.variant.h5}
        color={Typography.color.gray}
      >
        {dayjs().day(dayIndex).format("dd")}
      </Typography>
    );
  };

  const renderWeek = (days, index) => {
    return (
      <div key={index} className={styles["week"]}>
        {days.map(renderDay)}
      </div>
    );
  };

  return (
    <div className={styles["root"]}>
      <div className={styles["head"]}>
        <IconButton
          className={classnames(styles["arrow"], styles["prev"])}
          icon={EgwWebFont["chevron-left"]}
          disabled={minDate && dayjs(minDate).month() === monthDateInstance.month()}
          onClick={() => handleMonthChange(monthDateInstance.subtract(1, "month"))}
        />
        <Typography
          className={styles["title"]}
          variant={Typography.variant.h5}
          color={Typography.color.textInactive}
        >{dayjs(monthDate).format("MMMM YYYY")}</Typography>
        <IconButton
          className={classnames(styles["arrow"], styles["next"])}
          icon={EgwWebFont["chevron-right"]}
          disabled={maxDate && dayjs(maxDate).month() === monthDateInstance.month()}
          onClick={() => handleMonthChange(monthDateInstance.add(1, "month"))}
        />
      </div>
      <div className={styles["week-names"]}>
        {weekNames.map(renderWeekName)}
      </div>
      <div className={styles["body"]}>
        {weeks.map(renderWeek)}
      </div>
    </div>
  );
};

DatePicker.propTypes = {
  date: PropTypes.object,
  minDate: PropTypes.object,
  onDateChange: PropTypes.func,
};

export default DatePicker;

